import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
  Button,
  Spinner,
  Nav,
  NavItem,
  NavLink
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import api from '../../../helpers/api';
import { formatCreatedDate } from '../../../constants/utils';
import { useTranslation } from 'react-i18next';



// Notification type specific colors
const getTypeColor = (notificationType) => {
  if (!notificationType) return null;
  if (notificationType.includes('DEACTIVATED')) return '#F44336';
  if (notificationType.includes('REACTIVATED')) return '#4CAF50';
  if (notificationType.includes('ASSIGNED') || notificationType.includes('ADDED')) return '#4CAF50';
  if (notificationType.includes('CHANGED')) return '#FF9800';
  if (notificationType.includes('COMPLIANCE') || notificationType.includes('INACTIVE') || notificationType.includes('QUARTERLY')) return '#FF5722';
  if (notificationType.includes('COMPLETED') || notificationType.includes('CLOSED')) return '#4CAF50';
  if (notificationType.includes('CREATED') || notificationType.includes('ACCEPTED')) return '#2196F3';
  return null;
};

const NotificationDropdown = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Category icon and color mapping for RBAC notifications
  const categoryConfig = useMemo(() => ({
    role: { icon: 'user-check', color: '#4CAF50', label: t('shared.role', 'Role') },
    account: { icon: 'shield', color: '#2196F3', label: t('shared.account', 'Account') },
    service_request: { icon: 'clipboard', color: '#FF9800', label: t('shared.service_requests', 'Service Requests') },
    work_order: { icon: 'tool', color: '#3F51B5', label: t('shared.work_orders', 'Work Orders') },
    admin: { icon: 'users', color: '#9C27B0', label: t('shared.admin', 'Admin') },
    compliance: { icon: 'alert-triangle', color: '#F44336', label: t('shared.compliance', 'Compliance') },
    escalation: { icon: 'trending-up', color: '#E91E63', label: t('shared.escalation', 'Escalation') },
    asset: { icon: 'alert-octagon', color: '#F44336', label: t('shared.asset', 'Asset') },
    meter: { icon: 'activity', color: '#00BCD4', label: t('shared.meter', 'Meter') },
    tool: { icon: 'tool', color: '#795548', label: t('shared.tool', 'Tool') },
    spare_part: { icon: 'box', color: '#FF6B6B', label: t('shared.spare_part', 'Spare Part') },
    materiovigilance: { icon: 'alert-circle', color: '#FF5722', label: t('shared.materiovigilance', 'Materiovigilance') },
    team: { icon: 'users', color: '#673AB7', label: t('shared.team', 'Team') },
    asset_transfer: { icon: 'repeat', color: '#3980c0', label: t('business.planning.transfers', 'Transfers') },
    default: { icon: 'bell', color: '#607D8B', label: t('shared.general', 'General') }
  }), [t]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(null);
  const [displayCount, setDisplayCount] = useState(10);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const authUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = authUser?.activeOrganizationId ?? null;
  const userId = authUser?.id;
  // In Global View there is no active org, so we omit organizationId and the backend
  // aggregates notifications across all the user's organizations (membership-scoped).
  const isGlobalView = useSelector((s) => !!s.ActiveContext?.isGlobalView);

  const toggle = () => setDropdownOpen(prevState => !prevState);

  // Get the config for a notification's category
  const getCategoryConfig = (category) => {
    return categoryConfig[category] || categoryConfig.default;
  };

  // Filter notifications by category
  const filteredNotifications = useMemo(() => {
    if (categoryFilter === 'all') return notifications;
    return notifications.filter(n => n.category === categoryFilter);
  }, [notifications, categoryFilter]);

  // Get unique categories from notifications
  const availableCategories = useMemo(() => {
    const categories = new Set(notifications.map(n => n.category).filter(Boolean));
    return ['all', ...Array.from(categories)];
  }, [notifications]);

  useEffect(() => {
    getNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalView]);

  const getNotifications = async () => {
    try {
      setLoading(true);
      const url = organizationId
        ? `site-notifications/user/${userId}?organizationId=${organizationId}`
        : `site-notifications/user/${userId}`;
      const { data } = await api.get(url);
      setNotifications(data || []);
      setDisplayCount(10); // Reset display count when refreshing
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      setMarkingAsRead(notificationId);

      const notification = notifications.find(n => n.id === notificationId);
      if (!notification) return;

      const data = {
        id: notification.id,
        read: true, // Mark as read
      };

      await api.put('site-notifications/update', data);

      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, read: true }
            : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => prev + 10);
      setLoadingMore(false);
    }, 300);
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      const params = organizationId
        ? `organizationId=${organizationId}&userId=${userId}`
        : `userId=${userId}`;
      await api.put(`site-notifications/mark-all-read?${params}`);
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse metadata to get work order ticket number for navigation
  const getWorkOrderTicket = (notification) => {
    if (!notification.metadata) return null;
    try {
      const meta = typeof notification.metadata === 'string'
        ? JSON.parse(notification.metadata)
        : notification.metadata;
      return meta.workOrderTicketNumber || null;
    } catch {
      return null;
    }
  };

  const handleViewWorkOrder = (notification) => {
    const ticket = getWorkOrderTicket(notification);
    if (ticket) {
      setDropdownOpen(false);
      navigate(`/reactive?ticket=${encodeURIComponent(ticket)}`);
    }
  };

  // Transfer notifications carry the planning entry's id so the queue can open it directly —
  // an incoming transfer is useless as an alert if the admin then has to hunt for the request.
  const getTransferRequestId = (notification) => {
    if (!notification.metadata) return null;
    try {
      const meta = typeof notification.metadata === 'string'
        ? JSON.parse(notification.metadata)
        : notification.metadata;
      return meta.equipmentRequestId || null;
    } catch {
      return null;
    }
  };

  const handleViewTransfer = (notification) => {
    const requestId = getTransferRequestId(notification);
    if (requestId) {
      setDropdownOpen(false);
      navigate(`/planning?tab=transfers&request=${encodeURIComponent(requestId)}`);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const filteredUnreadCount = filteredNotifications.filter(n => !n.read).length;
  const displayNotifications = filteredNotifications.slice(0, displayCount);
  const hasMore = displayCount < filteredNotifications.length;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin-animation {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
      <Dropdown isOpen={dropdownOpen} toggle={toggle} className="d-none d-sm-inline-block">
      <DropdownToggle
        tag="button"
        className="btn header-item position-relative"
        style={{ background: 'none', border: 'none' }}
      >
        <FeatherIcon icon="bell" className="icon-sm" />
        {unreadCount > 0 && (
          <span
            className="badge badge-sm"
            style={{
                fontSize: '10px',
                minWidth: '18px',
                height: '18px',
                borderRadius: '50%',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: '15px',
                right: '4px',
                transform: 'none',
                backgroundColor: 'red',
                color: 'white',
                zIndex: 1,
                pointerEvents: 'none'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </DropdownToggle>

      <DropdownMenu
        end
        className="dropdown-menu-lg p-0"
        style={{ minWidth: '350px', maxHeight: '400px', overflowY: 'auto' }}
      >
        <div className="p-3 border-bottom">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="m-0">{t('shared.notifications', 'Notifications')}</h6>
            <div className="d-flex align-items-center gap-2">
              {unreadCount > 0 && (
                <>
                  <Badge color="primary">{unreadCount} {t('shared.unread', 'unread')}</Badge>
                  <Button
                    size="sm"
                    color="outline-secondary"
                    onClick={markAllAsRead}
                    disabled={loading}
                    title={t('business.components.notification_dropdown.mark_all_as_read', 'Mark all as read')}
                    style={{ fontSize: '10px', padding: '2px 6px' }}
                  >
                    {t('business.components.notification_dropdown.mark_all_as_read_short', 'Mark all read')}
                  </Button>
                </>
              )}
              <Button
                size="sm"
                color="link"
                className="p-0 text-primary"
                onClick={getNotifications}
                disabled={loading}
                title={t('business.components.notification_dropdown.refresh', 'Refresh notifications')}
                style={{
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FeatherIcon
                  icon="refresh-cw"
                  size={16}
                  className={loading ? 'spin-animation' : ''}
                />
              </Button>
            </div>
          </div>
          {/* Category filter tabs */}
          {availableCategories.length > 1 && (
            <Nav tabs className="border-0" style={{ fontSize: '11px' }}>
              {availableCategories.map(cat => (
                <NavItem key={cat}>
                  <NavLink
                    className={`py-1 px-2 ${categoryFilter === cat ? 'active' : ''}`}
                    onClick={() => { setCategoryFilter(cat); setDisplayCount(10); }}
                    style={{ cursor: 'pointer', fontSize: '11px' }}
                  >
                    {cat === 'all' ? t('shared.all', 'All') : (categoryConfig[cat]?.label || cat)}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          )}
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner size="sm" color="primary" />
            <p className="text-muted mt-2 mb-0">{t('business.components.notification_dropdown.loading', 'Loading notifications...')}</p>
          </div>
        ) : displayNotifications.length === 0 ? (
          <div className="text-center py-4">
            <FeatherIcon icon="bell-off" size="32" className="text-muted mb-2" />
            <p className="text-muted mb-0">{t('business.components.notification_dropdown.empty', 'No notifications')}</p>
          </div>
        ) : (
          <div className="notification-list">
            {displayNotifications.map((notification, index) => (
              <DropdownItem
                key={notification.id}
                tag="div"
                className={`p-3 border-bottom ${!notification.read ? 'bg-light' : ''}`}
                style={{
                  cursor: 'default',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}
              >
                <div className="d-flex align-items-start">
                  <div className="flex-shrink-0 me-3">
                    {(() => {
                      const catConfig = getCategoryConfig(notification.category);
                      const typeColor = getTypeColor(notification.notificationType);
                      const bgColor = notification.read
                        ? '#f8f9fa'
                        : (typeColor || catConfig.color);
                      return (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: bgColor,
                            color: notification.read ? '#6c757d' : '#fff'
                          }}
                        >
                          <FeatherIcon
                            icon={notification.read ? "check" : catConfig.icon}
                            size="16"
                          />
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <div>
                        <h6 className={`mb-0 ${!notification.read ? 'fw-bold' : ''}`} style={{ fontSize: '14px' }}>
                          {notification.title}
                        </h6>
                        {notification.category && (
                          <small
                            className="text-uppercase"
                            style={{
                              fontSize: '9px',
                              color: getCategoryConfig(notification.category).color,
                              fontWeight: 600
                            }}
                          >
                            {getCategoryConfig(notification.category).label}
                          </small>
                        )}
                        {isGlobalView && notification.organization?.name && (
                          <div>
                            <small className="text-muted" style={{ fontSize: '9px' }}>
                              <FeatherIcon icon="briefcase" size="9" className="me-1" />
                              {notification.organization.name}
                            </small>
                          </div>
                        )}
                      </div>
                      {!notification.read && (
                        <div className="ms-2 flex-shrink-0">
                          <Button
                            size="sm"
                            color="outline-primary"
                            style={{
                              fontSize: '10px',
                              padding: '2px 8px',
                              height: '24px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            disabled={markingAsRead === notification.id}
                          >
                            {markingAsRead === notification.id ? (
                              <Spinner size="sm" style={{ width: '12px', height: '12px' }} />
                            ) : (
                              t('business.components.notification_dropdown.mark_as_read', 'Mark as Read')
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="text-muted mb-1" style={{ fontSize: '13px', lineHeight: '1.4' }}>
                      {notification.message}
                    </p>
                    <div className="d-flex align-items-center justify-content-between">
                      <small className="text-muted" style={{ fontSize: '11px' }}>
                        {formatCreatedDate(notification.createdAt)}
                      </small>
                      {getWorkOrderTicket(notification) && (
                        <Button
                          size="sm"
                          color="link"
                          className="p-0 text-primary"
                          style={{
                            fontSize: '11px',
                            textDecoration: 'underline'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewWorkOrder(notification);
                          }}
                        >
                          {t('shared.view_work_order', 'View Work Order')}
                          <FeatherIcon icon="external-link" size="10" className="ms-1" />
                        </Button>
                      )}
                      {!getWorkOrderTicket(notification) && getTransferRequestId(notification) && (
                        <Button
                          size="sm"
                          color="link"
                          className="p-0 text-primary"
                          style={{
                            fontSize: '11px',
                            textDecoration: 'underline'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTransfer(notification);
                          }}
                        >
                          {t('business.planning.view_transfer', 'View Transfer')}
                          <FeatherIcon icon="external-link" size="10" className="ms-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </DropdownItem>
            ))}
          </div>
        )}

        {hasMore && (
          <div className="p-2 border-top text-center">
            <Button
              size="sm"
              color="link"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="text-primary"
            >
              {loadingMore ? (
                <>
                  <Spinner size="sm" className="me-1" style={{ width: '12px', height: '12px' }} />
                  {t('shared.loading', 'Loading...')}
                </>
              ) : (
                <>
                  {t('shared.load_more_count', 'Load More ({{count}} more)', { count: notifications.length - displayCount })}
                </>
              )}
            </Button>
          </div>
        )}

        {!hasMore && notifications.length > 10 && (
          <div className="p-2 border-top text-center">
            <small className="text-muted">
              {t('business.components.notification_dropdown.all_loaded', 'All {{count}} notifications loaded', { count: notifications.length })}
            </small>
          </div>
        )}
      </DropdownMenu>
    </Dropdown>
    </>
  );
};

export default NotificationDropdown;